import { Card, DataList, ErrorView, LoadingView, StatusBadge, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { FeedbackMessage } from '../../components/dashboard/FeedbackMessage';
import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { Button, Label, Textbox, useAuth } from '@app/engine';
import { CommonConstants, GetLocaleDataForAgents, IsValidE164, IsValidEmail, Logger, NormalizePhoneE164, WrapLTR } from '@shared/common';
import { GetUserGeoTimezone, NicheContentClient, Telemetry, TenantClient, UserClient } from '@shared/frontend';
import { useEffect, useMemo, useState } from 'react';
import type { IFeedbackDTO } from '../../components/dashboard/FeedbackMessage';
import type { INicheContentDTO, ITenantUserDTO } from '@shared/common';

/** File-local constants. */
const localConstants = {
  logContext: 'Account',
  settingUsername: 'username',
  settingPassword: 'password',
  settingBusiness: 'business',
  settingOwner: 'owner',
  emptyNiches: [] as INicheContentDTO[]
} as const;

/** Minimal typing for Intl.supportedValuesOf, which is ES2022 (this project targets ES2020 libs, but evergreen runtimes provide it). */
interface IIntlSupportedValues {
  supportedValuesOf?: (key: string) => string[];
}

/**
 * Lists the IANA timezone identifiers supported by the runtime.
 *
 * @returns The supported IANA timezone identifiers, or an empty array when the runtime lacks Intl.supportedValuesOf.
 */
function GetTimezones(): string[] {
  const intl = Intl as unknown as IIntlSupportedValues;
  return typeof intl.supportedValuesOf === 'function' ? intl.supportedValuesOf('timeZone') : [];
}

/**
 * Maps a tenant-user role to its display label.
 *
 * @param role - The tenant-user role.
 * @returns The localized display label for the role.
 */
function RoleLabel(role: ITenantUserDTO['role']): string {
  return role === CommonConstants.Database.UserRole.Admin ? Localized.AccountRoleAdmin : Localized.AccountRoleEmployee;
}

/**
 * Help panel: explains how the account data is used and where to get assistance.
 *
 * @returns The help panel element.
 */
export function AccountPanel() {
  const { user, updateUser, updatePassword, sendEmailVerification, reloadUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState(user?.username ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);
  const [resent, setResent] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Keep the username field in sync with the account while the panel is not being edited.
  useEffect(() => {
    if (!editing) setUsername(user?.username ?? '');
  }, [user?.username, editing]);

  const clearPasswords = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const startEdit = () => {
    setUsername(user?.username ?? '');
    clearPasswords();
    setFeedback(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setUsername(user?.username ?? '');
    clearPasswords();
    setFeedback(null);
    setEditing(false);
  };

  const onSave = async () => {
    if (saving || !user) return;
    // The password change is optional, but when any password field is filled all three are required and the confirmation must match the new password.
    const wantsPassword = newPassword.length > 0 || currentPassword.length > 0 || confirmPassword.length > 0;
    if (wantsPassword && newPassword !== confirmPassword) {
      setFeedback({ text: Localized.AccountPasswordMismatch, type: 'error' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const trimmed = username.trim();
      if (trimmed.length > 0 && trimmed !== user.username) {
        const controller = new AbortController();
        const updated = await UserClient.UpdateUser(controller.signal, user.id, { username: trimmed });
        updateUser(updated);
        Telemetry.SettingsChanged(localConstants.settingUsername);
      }
      if (wantsPassword) {
        await updatePassword(currentPassword, newPassword);
        Telemetry.SettingsChanged(localConstants.settingPassword);
      }
      clearPasswords();
      setFeedback({ text: Localized.DashSaved, type: 'success' });
      setEditing(false);
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: Localized.AccountSaveError, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const onResend = async () => {
    if (verifying) return;
    setVerifying(true);
    try {
      await sendEmailVerification();
      setResent(true);
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  const onRefresh = async () => {
    if (verifying) return;
    setVerifying(true);
    try {
      await reloadUser();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  if (!user)
    return (
      <Card title={Localized.AccountDetailsTitle}>
        <LoadingView className={Styles.panelLoading} />
      </Card>
    );

  // Email verification control: a "Verified" pill badge once confirmed (matching the Status badges elsewhere); otherwise a Resend action that becomes Refresh after the verification email is sent.
  const verification = user.emailVerified ? (
    <Label classNameContainer={`${Styles.badge} ${Styles.badgeSuccess}`} text={Localized.AccountVerified} />
  ) : (
    <Button
      text={resent ? Localized.SharedRefresh : Localized.AccountResend}
      type="button"
      disabled={verifying}
      classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.btnFixedWide}`}
      classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall} ${Styles.btnFixedWide}`}
      onClickHandler={() => void (resent ? onRefresh() : onResend())}
    />
  );

  const actions = !editing ? <Button text={Localized.AccountEdit} type="button" classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`} onClickHandler={startEdit} /> : null;

  return (
    <Card title={Localized.AccountDetailsTitle} actions={actions} feedback={<FeedbackMessage feedback={feedback} />}>
      {editing ? (
        <>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.AccountUsername} />
            <Textbox value={username} autoComplete="username" onChange={(value) => setUsername(value)} classNameText={Styles.input} />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedEmail} />
            <Label classNameContainer={Styles.inputReadonly} text={user.email} />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.AccountEmailStatus} />
            <div className={Styles.fieldBadgeBox}>{verification}</div>
          </div>
          <div className={`${Styles.subPanel} ${Styles.passwordPanel}`}>
            <Label classNameContainer={Styles.statLabel} text={Localized.AccountChangePassword} />
            <Label classNameContainer={Styles.muted} text={Localized.AccountChangePasswordHint} />
          </div>
          <div className={`${Styles.subPanel} ${Styles.passwordFields}`}>
            <div className={Styles.field}>
              <Label classNameContainer={Styles.label} text={Localized.AccountCurrentPassword} />
              <Textbox
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(value) => {
                  setCurrentPassword(value);
                  setFeedback(null);
                }}
                classNameText={Styles.input}
              />
            </div>
            <div className={Styles.field}>
              <Label classNameContainer={Styles.label} text={Localized.AccountNewPassword} />
              <Textbox
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(value) => {
                  setNewPassword(value);
                  setFeedback(null);
                }}
                classNameText={Styles.input}
              />
            </div>
            <div className={Styles.field}>
              <Label classNameContainer={Styles.label} text={Localized.AccountConfirmPassword} />
              <Textbox
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(value) => {
                  setConfirmPassword(value);
                  setFeedback(null);
                }}
                classNameText={Styles.input}
              />
            </div>
          </div>
          <div className={Styles.formActions}>
            <Button
              text={saving ? Localized.DashSaving : Localized.DashSave}
              type="button"
              disabled={saving}
              classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
              classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
              onClickHandler={() => void onSave()}
            />
            <Button
              text={Localized.SharedCancel}
              type="button"
              disabled={saving}
              classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
              classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
              onClickHandler={cancelEdit}
            />
          </div>
        </>
      ) : (
        <DataList
          rows={[
            { label: Localized.AccountUsername, value: user.username },
            { label: Localized.SharedEmail, value: user.email },
            { label: Localized.AccountEmailStatus, value: verification }
          ]}
        />
      )}
    </Card>
  );
}

/**
 * Business panel: the tenant's business name, industry, timezone, and default language, with a read-only status indicator.
 *
 * @returns The business panel element.
 */
export function BusinessPanel() {
  const { selectedLang, languages } = useLocale();
  const { data: tenant, loading, error, reload } = useDashboardData((signal) => TenantClient.GetMyTenant(signal), []);
  const nichesResult = useDashboardData((signal) => NicheContentClient.ListPublicNiches(signal, selectedLang), [selectedLang]);
  const niches = nichesResult.data ?? localConstants.emptyNiches;
  const timezones = useMemo(() => GetTimezones(), []);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', niche: '', timezone: '', defaultLocale: '' });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);

  useEffect(() => {
    if (tenant) setForm({ name: tenant.name, niche: tenant.niche, timezone: tenant.timezone || GetUserGeoTimezone(), defaultLocale: tenant.defaultLocale });
  }, [tenant]);

  const nicheLabel = useMemo(() => niches.find((entry) => entry.niche === tenant?.niche)?.signupLabel ?? tenant?.niche ?? '', [niches, tenant?.niche]);
  const localeLabel = useMemo(() => languages.find((lang) => lang.code === tenant?.defaultLocale)?.label ?? tenant?.defaultLocale ?? '', [languages, tenant?.defaultLocale]);
  // The Agent-language picker only offers languages the voice agent can actually take calls in (Deepgram-transcribable); the UI display-language switcher still lists them all.
  const agentLanguages = useMemo(() => {
    const supported = new Set(GetLocaleDataForAgents().map((locale) => locale.Code));
    return languages.filter((lang) => supported.has(lang.code));
  }, [languages]);

  const onSave = async () => {
    if (saving) return;
    if (form.name.trim().length === 0 || form.niche.length === 0 || form.timezone.length === 0) {
      setFeedback({ text: Localized.AccountBusinessErrorRequired, type: 'error' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const controller = new AbortController();
      await TenantClient.UpdateMyTenant(controller.signal, { name: form.name.trim(), niche: form.niche, timezone: form.timezone, defaultLocale: form.defaultLocale });
      Telemetry.SettingsChanged(localConstants.settingBusiness);
      setFeedback({ text: Localized.DashSaved, type: 'success' });
      setEditing(false);
      reload();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: Localized.AccountSaveError, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (tenant) setForm({ name: tenant.name, niche: tenant.niche, timezone: tenant.timezone || GetUserGeoTimezone(), defaultLocale: tenant.defaultLocale });
    setEditing(false);
  };

  if (loading)
    return (
      <Card title={Localized.AccountBusiness}>
        <LoadingView className={Styles.panelLoading} />
      </Card>
    );
  if (error || !tenant)
    return (
      <Card title={Localized.AccountBusiness}>
        <ErrorView onRetry={reload} />
      </Card>
    );

  const actions = !editing ? (
    <Button
      text={Localized.AccountEdit}
      type="button"
      classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
      onClickHandler={() => {
        setFeedback(null);
        setEditing(true);
      }}
    />
  ) : null;

  return (
    <Card title={Localized.AccountBusiness} actions={actions} feedback={<FeedbackMessage feedback={feedback} />}>
      {editing ? (
        <>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedBusinessName} />
            <Textbox value={form.name} onChange={(value) => setForm((prev) => ({ ...prev, name: value }))} classNameText={Styles.input} />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedIndustry} />
            <select className={Styles.input} value={form.niche} onChange={(event) => setForm((prev) => ({ ...prev, niche: event.target.value }))}>
              {form.niche.length > 0 && !niches.some((entry) => entry.niche === form.niche) ? <option value={form.niche}>{form.niche}</option> : null}
              {niches.map((entry) => (
                <option key={entry.niche} value={entry.niche}>
                  {entry.signupLabel}
                </option>
              ))}
            </select>
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.AccountTimezone} />
            <select className={Styles.input} value={form.timezone} onChange={(event) => setForm((prev) => ({ ...prev, timezone: event.target.value }))}>
              {form.timezone.length > 0 && !timezones.includes(form.timezone) ? <option value={form.timezone}>{form.timezone}</option> : null}
              {timezones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.AccountLocale} />
            <select className={Styles.input} value={form.defaultLocale} onChange={(event) => setForm((prev) => ({ ...prev, defaultLocale: event.target.value }))}>
              {/* Preserve an already-stored value that is no longer an offered agent language so opening the form never silently changes it. */}
              {form.defaultLocale.length > 0 && !agentLanguages.some((lang) => lang.code === form.defaultLocale) ? <option value={form.defaultLocale}>{localeLabel}</option> : null}
              {agentLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedStatus} />
            <div className={Styles.fieldBadgeBox}>
              <StatusBadge status={tenant.status} />
            </div>
          </div>
          <div className={Styles.formActions}>
            <Button
              text={saving ? Localized.DashSaving : Localized.DashSave}
              type="button"
              disabled={saving}
              classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
              classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
              onClickHandler={() => void onSave()}
            />
            <Button
              text={Localized.SharedCancel}
              type="button"
              disabled={saving}
              classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
              classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
              onClickHandler={cancelEdit}
            />
          </div>
        </>
      ) : (
        <DataList
          rows={[
            { label: Localized.SharedBusinessName, value: tenant.name },
            { label: Localized.SharedIndustry, value: nicheLabel },
            { label: Localized.AccountTimezone, value: tenant.timezone },
            { label: Localized.AccountLocale, value: localeLabel },
            { label: Localized.SharedStatus, value: <StatusBadge status={tenant.status} /> }
          ]}
        />
      )}
    </Card>
  );
}

/**
 * Owner panel: the single owner team member's display name, phone, and contact email, with read-only role and status.
 *
 * @returns The owner panel element.
 */
export function OwnerPanel() {
  const { tenantUser, refreshTenant } = useDashboard();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ displayName: '', phoneE164: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<IFeedbackDTO | null>(null);

  useEffect(() => {
    if (tenantUser) setForm({ displayName: tenantUser.displayName, phoneE164: tenantUser.phoneE164 ?? '', email: tenantUser.email });
  }, [tenantUser]);

  if (!tenantUser)
    return (
      <Card title={Localized.AccountOwner}>
        <LoadingView className={Styles.panelLoading} />
      </Card>
    );

  const owner = tenantUser;

  const onSave = async () => {
    if (saving) return;
    // Client-side format validation mirrors the server's rejects so the user gets an immediate, specific message instead of a generic save error. Loose input (dashes, dots, parentheses) is
    // normalized to E.164 first, so `(206) 822-2207` saves as `+12068222207`.
    const phone = NormalizePhoneE164(form.phoneE164);
    if (phone.length > 0 && !IsValidE164(phone)) {
      setFeedback({ text: Localized.AccountErrorPhone, type: 'error' });
      return;
    }
    const email = form.email.trim();
    if (email.length === 0 || !IsValidEmail(email)) {
      setFeedback({ text: Localized.AccountErrorEmail, type: 'error' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      const controller = new AbortController();
      await TenantClient.UpdateMe(controller.signal, { displayName: form.displayName.trim(), phoneE164: phone, email });
      Telemetry.SettingsChanged(localConstants.settingOwner);
      setFeedback({ text: Localized.DashSaved, type: 'success' });
      setEditing(false);
      refreshTenant();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setFeedback({ text: Localized.AccountSaveError, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setForm({ displayName: owner.displayName, phoneE164: owner.phoneE164 ?? '', email: owner.email });
    setEditing(false);
  };

  const actions = !editing ? (
    <Button
      text={Localized.AccountEdit}
      type="button"
      classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
      onClickHandler={() => {
        setFeedback(null);
        setEditing(true);
      }}
    />
  ) : null;

  return (
    <Card title={Localized.AccountOwner} actions={actions} feedback={<FeedbackMessage feedback={feedback} />}>
      {editing ? (
        <>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.AccountDisplayName} />
            <Textbox value={form.displayName} onChange={(value) => setForm((prev) => ({ ...prev, displayName: value }))} classNameText={Styles.input} />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedContactPhone} />
            <Textbox
              value={form.phoneE164}
              onChange={(value) => setForm((prev) => ({ ...prev, phoneE164: value }))}
              onBlur={() => setForm((prev) => ({ ...prev, phoneE164: NormalizePhoneE164(prev.phoneE164) }))}
              classNameText={Styles.input}
            />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedContactEmail} />
            <Textbox type="email" autoComplete="email" value={form.email} onChange={(value) => setForm((prev) => ({ ...prev, email: value }))} classNameText={Styles.input} />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.AccountRole} />
            <Label classNameContainer={Styles.inputReadonly} text={RoleLabel(owner.role)} />
          </div>
          <div className={Styles.field}>
            <Label classNameContainer={Styles.label} text={Localized.SharedStatus} />
            <div className={Styles.fieldBadgeBox}>
              <StatusBadge status={owner.status} />
            </div>
          </div>
          <div className={Styles.formActions}>
            <Button
              text={saving ? Localized.DashSaving : Localized.DashSave}
              type="button"
              disabled={saving}
              classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
              classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary} ${Styles.btnSmall}`}
              onClickHandler={() => void onSave()}
            />
            <Button
              text={Localized.SharedCancel}
              type="button"
              disabled={saving}
              classNameButtonEnabled={`${Styles.btn} ${Styles.btnSmall}`}
              classNameButtonDisabled={`${Styles.btn} ${Styles.btnSmall}`}
              onClickHandler={cancelEdit}
            />
          </div>
        </>
      ) : (
        <DataList
          rows={[
            { label: Localized.AccountDisplayName, value: owner.displayName },
            { label: Localized.SharedContactPhone, value: owner.phoneE164 ? WrapLTR(owner.phoneE164) : '—' },
            { label: Localized.SharedContactEmail, value: owner.email },
            { label: Localized.AccountRole, value: RoleLabel(owner.role) },
            { label: Localized.SharedStatus, value: <StatusBadge status={owner.status} /> }
          ]}
        />
      )}
    </Card>
  );
}

/**
 * Account screen: the help panel on the left with the Account, Business, and Owner settings panels stacked beside it (collapsing to a single column when space is restricted).
 *
 * @returns The account screen element.
 */
export function Account() {
  return (
    <div className={Styles.accountStack}>
      <AccountPanel />
      <OwnerPanel />
    </div>
  );
}
